import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  CalendarClock, Repeat, Clock, Pause, Play, Trash2, Loader2, AlertTriangle,
  ChevronRight, ExternalLink, RefreshCw,
} from 'lucide-react';
import {
  aiAssistantService,
  type AiSchedule,
  type AiScheduledRun,
} from '@/services/aiAssistantService';

/**
 * Cross-conversation manage view — every loop and one-shot schedule the
 * user owns. Companion to the in-conversation schedules row: same actions,
 * but for the "I just want to see every loop running for me" use case.
 * Selecting a schedule loads its recent run history on the right.
 */
export const AiSchedulesPage: React.FC = () => {
  const [rows, setRows] = useState<AiSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await aiAssistantService.listSchedules();
      setRows(all);
      // Keep the current selection if it still exists; otherwise drop it.
      setSelectedId((id) => (id && all.some((s) => s.id === id) ? id : null));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load schedules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const act = async (id: string, fn: () => Promise<unknown>) => {
    if (busyId) return;
    setBusyId(id);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="flex h-full p-4 gap-4">
      <div className="flex-1 min-w-0 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">AI Schedules</h1>
            <span className="text-xs text-muted-foreground">
              recurring loops + one-shot scheduled runs
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {error && (
          <Card className="px-3 py-2 text-xs text-destructive flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" /> {error}
          </Card>
        )}

        {loading && rows.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading schedules…
          </div>
        ) : rows.length === 0 ? (
          <Card className="p-6 text-sm text-muted-foreground text-center">
            No schedules yet. Ask the assistant inside a chat — e.g.
            "set up a loop that checks my positions every 30 minutes".
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wide text-muted-foreground bg-muted/30">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Name</th>
                  <th className="text-left px-3 py-2 font-medium">Kind</th>
                  <th className="text-left px-3 py-2 font-medium">Cadence</th>
                  <th className="text-left px-3 py-2 font-medium">Next</th>
                  <th className="text-left px-3 py-2 font-medium">Runs</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr
                    key={s.id}
                    className={`border-t cursor-pointer ${
                      selectedId === s.id ? 'bg-primary/5' : 'hover:bg-muted/40'
                    } ${s.enabled ? '' : 'opacity-60'}`}
                    onClick={() => setSelectedId(s.id)}>
                    <td className="px-3 py-2 font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        {s.kind === 'Recurring'
                          ? <Repeat className="h-3.5 w-3.5 text-primary" />
                          : <Clock className="h-3.5 w-3.5 text-primary" />}
                        {s.name}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">{s.kind}</td>
                    <td className="px-3 py-2 text-xs">
                      {s.kind === 'Recurring'
                        ? `every ${s.intervalMinutes}m`
                        : s.runAtUtc
                          ? new Date(s.runAtUtc).toLocaleString()
                          : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {s.enabled
                        ? s.nextRunAtUtc
                          ? new Date(s.nextRunAtUtc).toLocaleString()
                          : '—'
                        : <span className="text-muted-foreground">disabled</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {s.runCount}
                      {s.errorCount > 0 && (
                        <span className="text-destructive"> ({s.errorCount} err)</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <a
                          href={`#/dashboard/ai-assistant?conversation=${encodeURIComponent(s.conversationId)}`}
                          className="p-1 rounded hover:bg-muted"
                          title="Open conversation">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                        {s.enabled ? (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                                  title="Disable"
                                  disabled={busyId === s.id}
                                  onClick={() => void act(s.id, () => aiAssistantService.disableSchedule(s.id))}>
                            {busyId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Pause className="h-3 w-3" />}
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                                  title="Re-enable"
                                  disabled={busyId === s.id}
                                  onClick={() => void act(s.id, () => aiAssistantService.enableSchedule(s.id))}>
                            {busyId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:text-destructive"
                                title="Delete schedule"
                                disabled={busyId === s.id}
                                onClick={() => {
                                  if (window.confirm(`Delete schedule "${s.name}"? Past run history is kept.`))
                                    void act(s.id, () => aiAssistantService.deleteSchedule(s.id));
                                }}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>

      {/* Right pane: recent runs for the selected schedule. */}
      <div className="w-96 shrink-0">
        {selectedId
          ? <ScheduleRunsPane scheduleId={selectedId} />
          : (
            <Card className="p-6 text-sm text-muted-foreground text-center">
              Select a schedule to see its recent runs.
            </Card>
          )}
      </div>
    </div>
  );
};

const ScheduleRunsPane: React.FC<{ scheduleId: string }> = ({ scheduleId }) => {
  const [runs, setRuns] = useState<AiScheduledRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    aiAssistantService.listScheduleRuns(scheduleId, 20)
      .then((r) => { if (!cancelled) setRuns(r); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load runs'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scheduleId]);

  return (
    <Card className="flex flex-col h-full overflow-hidden">
      <div className="px-3 py-2 border-b text-sm font-semibold flex items-center gap-2">
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
        Recent runs
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {loading && <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</div>}
        {error && <p className="text-xs text-destructive">{error}</p>}
        {!loading && runs.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">No runs yet.</p>
        )}
        {runs.map((r) => (
          <div key={r.id} className={`rounded border p-2 text-xs ${r.ok ? '' : 'border-destructive/40 bg-destructive/5'}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">
                {new Date(r.startedAtUtc).toLocaleString()}
              </span>
              <span className={r.ok ? 'text-emerald-500' : 'text-destructive'}>
                {r.ok ? 'OK' : 'Failed'}
              </span>
            </div>
            {r.error && <p className="mt-1 text-destructive break-words">{r.error}</p>}
            {r.reply && (
              <p className="mt-1 text-muted-foreground line-clamp-3 break-words">{r.reply}</p>
            )}
            {r.toolsUsed.length > 0 && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                tools: {r.toolsUsed.join(', ')}
              </p>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
};

export default AiSchedulesPage;
